import React, { useCallback, useEffect, useMemo } from 'react';
import { DynamicTableStateless } from '@atlaskit/dynamic-table';
import type { SortField } from '@atlassian/jira-admin-pages-issue-type-schemes-directory/src/types.tsx';
import { HeadingCellWrapper } from '@atlassian/jira-admin-pages-issue-types-common/src/ui/index.tsx';
import { useIssueTypes } from '@atlassian/jira-admin-pages-issue-types-resources/src/controllers/issue-types/main.tsx';
import {
	toSortFieldFilterQsTransformer,
	toSortDirectionFilterQsTransformer,
} from '@atlassian/jira-admin-pages-issue-types-resources/src/services/issue-types/utils/transformers.tsx';
import type { SortDirection } from '@atlassian/jira-common-constants/src/sort-directions';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useRouter } from '@atlassian/react-resource-router';
import { getTableRows } from './body';
import { EmptyState } from './empty-state';
import { ErrorState } from './error-state';
import { getTableHead } from './head';
import { Container } from './styled';

const getPage = (startAt: number, maxResults: number) =>
	Math.floor(startAt / Math.max(maxResults, 1)) + 1;

export const IssueTypesTable = () => {
	const { formatMessage } = useIntl();
	const [
		{ isLoading, data, error },
		{ updateFilter, fetchIssueTypes, setInitialCsrRenderComplete },
	] = useIssueTypes();

	const { values = [], maxResults = 0, total = 0, startAt = 0 } = data || {};

	const [routerState, routerActions] = useRouter();

	const { createAnalyticsEvent } = useAnalyticsEvents();

	const tableHead = useMemo(() => getTableHead(HeadingCellWrapper, formatMessage), [formatMessage]);
	const tableRows = useMemo(() => getTableRows(values), [values]);

	const onSetPage = useCallback(
		(newPage: number) => {
			fireUIAnalytics(createAnalyticsEvent({}), 'pageNumber changed', {
				page: newPage,
			});
			updateFilter(routerState.location.search, { page: newPage }, routerActions.replace);
		},
		[createAnalyticsEvent, routerActions.replace, routerState.location.search, updateFilter],
	);
	const onSort = useCallback(
		({ key, sortOrder }: { key: SortField; sortOrder: SortDirection }) => {
			fireUIAnalytics(createAnalyticsEvent({}), 'dynamicTable sorted', 'issueTypeColumn', {
				columnName: key,
				sortOrder,
			});
			updateFilter(
				routerState.location.search,
				{ sortField: key, sortDirection: sortOrder, page: 1 },
				routerActions.replace,
			);
		},
		[createAnalyticsEvent, routerActions.replace, routerState.location.search, updateFilter],
	);

	useEffect(() => {
		fetchIssueTypes(routerState.location.search);
		setInitialCsrRenderComplete();
	}, [fetchIssueTypes, routerState.location.search, setInitialCsrRenderComplete]);

	return (
		<Container>
			<DynamicTableStateless
				testId="admin-pages-issue-types-directory.ui.table.dynamic-table-stateless"
				head={tableHead}
				rows={tableRows}
				rowsPerPage={maxResults}
				page={getPage(startAt, maxResults)}
				totalRows={total}
				sortKey={toSortFieldFilterQsTransformer(routerState.location.search)}
				sortOrder={toSortDirectionFilterQsTransformer(routerState.location.search)}
				isLoading={isLoading}
				emptyView={
					error ? (
						<ErrorState
							onRetry={() => {
								fetchIssueTypes(routerState.location.search);
							}}
						/>
					) : (
						<EmptyState />
					)
				}
				onSetPage={onSetPage}
				onSort={onSort}
				isFixedSize
			/>
		</Container>
	);
};
